import { ChevronLeft, ChevronRight, Clock, Calendar as IconoCalendario } from "lucide-react";
import { useMemo, useState } from "react";
import {
  BORDE_ESTADO,
  Card,
  FONDO_ESTADO,
  PageHeader,
  StatusDot,
} from "../../components/ui/patrones";
import { Aviso, Boton, Etiqueta, Modal } from "../../components/ui/primitivos";
import {
  DIAS_DE_LA_SEMANA,
  diasDeLaGrilla,
  etiquetaDelDia,
  mismoDia,
  sumarDias,
  sumarMeses,
  tituloDelPeriodo,
} from "../../lib/calendario";
import { fetchOrdenesCalendario, type OrdenListada } from "../../lib/consola-api";
import { useDatos } from "../../lib/useDatos";
import { agruparPorEntrega, etiquetaDeCasilla } from "./entregas";
import { PUNTO } from "./estados";
import { fecha as formatoFecha } from "./orden";

/**
 * Calendario de entregas — porteado de `SchedulerPage.tsx` (ADR 0047,
 * fidelidad visual revisada en ADR 0074).
 *
 * Agrupa por **`fecha_pactada_entrega`**, como allá: el calendario contesta
 * «qué me comprometí a entregar este día», no cuándo se creó ni cuándo se
 * imprimió.
 *
 * No tiene endpoint propio, tampoco allá: se dibuja sobre el mismo listado que
 * el tablero, así que hereda su filtro de visibilidad sin hacer nada.
 */
export function CalendarioPagina() {
  const ordenes = useDatos(fetchOrdenesCalendario);
  const [referencia, setReferencia] = useState(() => new Date());
  const [vista, setVista] = useState<"mes" | "semana">("mes");
  const [diaAbierto, setDiaAbierto] = useState<Date | null>(null);

  const hoy = new Date();
  const dias = useMemo(() => diasDeLaGrilla(referencia, vista), [referencia, vista]);

  // Una sola recorrida del listado, no 42 —una por casilla— cada vez que
  // alguien cambia de mes.
  const { porDia, sinFecha } = useMemo(
    () => agruparPorEntrega(ordenes.datos ?? []),
    [ordenes.datos],
  );

  const entregasDe = (fecha: Date): OrdenListada[] => porDia.get(fecha.getTime()) ?? [];

  const mover = (signo: 1 | -1) =>
    setReferencia((r) => (vista === "mes" ? sumarMeses(r, signo) : sumarDias(r, signo * 7)));

  const delDia = diaAbierto ? entregasDe(diaAbierto) : [];

  return (
    <div className="flex h-full flex-col space-y-4">
      <PageHeader
        titulo="Calendario de Producción"
        subtitulo="Visualiza y gestiona la planificación de trabajos por fecha de entrega."
        acciones={
          <div className="flex items-center gap-2">
            {/* Dos grupos con su borde, como allá: la vista es una cosa y la
                navegación es otra, y pegados sin separar se leen como seis
                botones del mismo control. */}
            <div className="flex items-center gap-1 rounded-md border border-neutral-200 bg-white p-1 shadow-sm">
              <Boton
                variante={vista === "mes" ? "secundario" : "fantasma"}
                className="h-7 px-2 text-xs"
                onClick={() => setVista("mes")}
              >
                Mes
              </Boton>
              <Boton
                variante={vista === "semana" ? "secundario" : "fantasma"}
                className="h-7 px-2 text-xs"
                onClick={() => setVista("semana")}
              >
                Semana
              </Boton>
            </div>
            <div className="flex items-center gap-1 rounded-md border border-neutral-200 bg-white p-1 shadow-sm">
              <Boton
                variante="fantasma"
                className="h-7 px-1.5"
                aria-label="Período anterior"
                onClick={() => mover(-1)}
              >
                <ChevronLeft aria-hidden="true" className="h-4 w-4" />
              </Boton>
              <Boton
                variante="fantasma"
                className="h-7 px-2 text-xs"
                onClick={() => setReferencia(new Date())}
              >
                Hoy
              </Boton>
              <Boton
                variante="fantasma"
                className="h-7 px-1.5"
                aria-label="Período siguiente"
                onClick={() => mover(1)}
              >
                <ChevronRight aria-hidden="true" className="h-4 w-4" />
              </Boton>
            </div>
          </div>
        }
      />

      {ordenes.error ? <Aviso>{ordenes.error}</Aviso> : null}

      <Card className="flex flex-1 flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-4 border-neutral-200 border-b px-6 py-4">
          <h2 className="flex items-center gap-2 font-semibold text-lg">
            <IconoCalendario aria-hidden="true" className="h-5 w-5 text-neutral-500" />
            <span className="capitalize">{tituloDelPeriodo(referencia, vista)}</span>
          </h2>
          {/* Las que no tienen fecha pactada no caen en ninguna casilla: si no
              se dijeran, desaparecerían del calendario sin que nadie lo note. */}
          {sinFecha > 0 ? (
            <span className="text-neutral-500 text-sm">
              {sinFecha} {sinFecha === 1 ? "OT sin fecha pactada" : "OTs sin fecha pactada"} — no
              aparecen acá
            </span>
          ) : null}
        </div>

        <div className="grid grid-cols-7 border-neutral-200 border-b bg-neutral-50">
          {DIAS_DE_LA_SEMANA.map((d) => (
            <div
              key={d}
              className="px-2 py-2 text-center font-medium text-neutral-500 text-xs uppercase tracking-wider"
            >
              {d}
            </div>
          ))}
        </div>

        <div className="grid flex-1 auto-rows-fr grid-cols-7">
          {dias.map(({ fecha, pertenece }) => {
            const entregas = entregasDe(fecha);
            const esHoy = mismoDia(fecha, hoy);
            return (
              <button
                type="button"
                key={fecha.getTime()}
                // Se abre siempre, tenga o no entregas: el diálogo vacío es la
                // respuesta a «¿qué entrego ese día?», y una casilla que no
                // reacciona no distingue «nada» de «no anda».
                onClick={() => setDiaAbierto(fecha)}
                aria-label={etiquetaDeCasilla(fecha, entregas.length)}
                // Un solo fondo y no dos clases encadenadas: `bg-white` y el
                // tinte de hoy tienen la misma especificidad, así que cuál gana
                // lo decide el ORDEN EN EL CSS generado, no el del string —
                // encadenadas, el tinte no se veía nunca.
                className={`min-h-[100px] cursor-pointer border-neutral-200 border-r border-b p-2 text-left align-top transition-colors hover:bg-neutral-100 ${
                  esHoy ? "bg-sky-50" : pertenece ? "bg-white" : "bg-neutral-50"
                } ${pertenece ? "" : "text-neutral-400"}`}
              >
                <div className="mb-1 flex items-center justify-between">
                  <span
                    className={`flex h-7 w-7 items-center justify-center rounded-full font-medium text-sm ${
                      esHoy
                        ? "bg-brand-600 text-white"
                        : pertenece
                          ? "text-neutral-700"
                          : "text-neutral-400"
                    }`}
                  >
                    {fecha.getDate()}
                  </span>
                  {entregas.length > 0 ? <Etiqueta>{entregas.length}</Etiqueta> : null}
                </div>

                <div className="mt-1 space-y-1">
                  {/* Tres y el resto contado: con ocho entregas, la casilla
                      empujaría la grilla y el mes dejaría de leerse. */}
                  {entregas.slice(0, 3).map((o) => (
                    <div
                      key={o.id}
                      // El estado va también en el `title` y completo en el
                      // diálogo del día: el borde de color solo no es
                      // información para quien no lo distingue.
                      title={`${o.estado.replaceAll("_", " ").toLowerCase()}`}
                      className={`truncate rounded border border-neutral-200 border-l-2 bg-white px-1.5 py-0.5 text-[10px] shadow-sm ${
                        BORDE_ESTADO[PUNTO[o.estado] ?? "borrador"]
                      }`}
                    >
                      {o.numero} - {o.cliente}
                    </div>
                  ))}
                  {entregas.length > 3 ? (
                    <div className="pl-1 text-[10px] text-neutral-400">
                      + {entregas.length - 3} más
                    </div>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      {diaAbierto ? (
        <Modal abierto titulo={etiquetaDelDia(diaAbierto)} onCerrar={() => setDiaAbierto(null)}>
          {delDia.length === 0 ? (
            <div className="py-8 text-center text-neutral-500">
              <Clock aria-hidden="true" className="mx-auto mb-2 h-10 w-10 opacity-20" />
              <p>No hay entregas programadas para este día.</p>
            </div>
          ) : (
            <ul className="space-y-4 py-2">
              {delDia.map((o) => (
                <li
                  key={o.id}
                  className="flex items-start gap-3 rounded-lg border border-neutral-200 bg-white p-3 shadow-sm"
                >
                  <div
                    aria-hidden="true"
                    className={`w-1.5 self-stretch rounded-full ${FONDO_ESTADO[PUNTO[o.estado] ?? "borrador"]}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <h4 className="truncate font-semibold text-sm">
                        {o.numero} - {o.producto}
                      </h4>
                      <span className="flex shrink-0 items-center gap-1 text-neutral-600 text-xs">
                        <StatusDot estado={PUNTO[o.estado] ?? "borrador"} />
                        {o.estado.replaceAll("_", " ").toLowerCase()}
                      </span>
                    </div>
                    <p className="truncate text-neutral-500 text-sm">{o.cliente}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-neutral-500 text-xs">
                      <span className="flex items-center gap-1">
                        <Clock aria-hidden="true" className="h-3 w-3" />
                        {formatoFecha(o.fechaPactadaEntrega)}
                      </span>
                      <span>•</span>
                      <span>Cant: {o.cantidad}</span>
                      {o.responsable ? (
                        <>
                          <span>•</span>
                          <span>{o.responsable}</span>
                        </>
                      ) : null}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="flex justify-end pt-4">
            <Boton variante="secundario" onClick={() => setDiaAbierto(null)}>
              Cerrar
            </Boton>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
